import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { advertisementService } from '../../../services/advertisementService';
import { useAuth } from '../../../contexts/AuthContext';

const sanitizeBannerUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  return url.replace(/ /g, '%20');
};

const parseHeight = (val) => {
  if (!val) return null;
  const clean = val.split('::')[0].trim();
  if (clean === 'auto') return 'auto';
  if (clean === 'row-height') return '320px';
  if (/^\d+$/.test(clean)) return `${clean}px`;
  if (/^\d+px$/i.test(clean)) return clean;
  if (/^\d+%$/i.test(clean)) return clean;
  if (/^\d+vh$/i.test(clean)) return clean;
  return clean;
};

const BannerAdPanel = ({ banner, isAuthenticated, onAuthRequired, isMulti = false, trackHeight = null }) => {
  const panelRef = useRef(null);
  const recordedRef = useRef(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!banner || recordedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !recordedRef.current) {
          advertisementService.recordImpression(banner.id, user?.id);
          recordedRef.current = true;
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (panelRef.current) {
      observer.observe(panelRef.current);
    }

    return () => observer.disconnect();
  }, [banner, user]);

  const handleClick = (e) => {
    advertisementService.recordClick(banner.id, user?.id);

    if (banner.actionType === 'modal') {
      e?.preventDefault?.();
      console.log('Inline banner clicked (modal action not fully implemented for inline)');
    }
  };

  if (!banner) return null;

  const hasButton = !!banner.actionButtonText?.trim();
  const imgPos = banner.imagePosition || banner.image_position || 'center';
  const textPos = banner.textPosition || banner.text_position || 'left';
  const overlayShade = banner.overlayShade || banner.overlay_shade || 'none';
  const adWidth = banner.adWidth || banner.ad_width || 'auto';

  const isFullWidth = adWidth === 'full';

  // Both ads in the frame share the EXACT same height in pixels!
  const panelHeight = parseHeight(banner.heightStyle || banner.height_style) || trackHeight || '320px';

  // Image alignment within its container
  const effectiveImgPos = imgPos || 'center';
  const containerJustifyClass = 
    effectiveImgPos === 'left' ? 'justify-start' :
    effectiveImgPos === 'right' ? 'justify-end' :
    'justify-center';

  const PanelContent = (
    <div 
      ref={panelRef}
      className={`relative h-full ${isFullWidth ? 'w-full' : 'w-auto'} flex-shrink-0 overflow-hidden flex items-center ${containerJustifyClass} ${!hasButton && banner.targetLink ? 'cursor-pointer' : ''}`}
      style={{
        height: panelHeight,
      }}
    >
      {/* 1. Ad Image: Locked at full banner frame height, natural full width, zero clipping, zero rescaling! */}
      <img 
        src={sanitizeBannerUrl(banner.backgroundImageUrl)}
        alt={banner.title || 'Banner'}
        className={`h-full ${isFullWidth ? 'w-auto max-w-full object-contain mx-auto' : 'w-auto max-w-none object-contain'} block select-none`}
        style={{
          filter: overlayShade === 'dark' 
            ? 'brightness(0.7)' 
            : (overlayShade === 'medium' ? 'brightness(0.85)' : 'none')
        }}
      />

      {/* 2. Configurable Overlay directly on the image */}
      {(!overlayShade || overlayShade === 'none') ? null :
       overlayShade === 'subtle' ? (
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent pointer-events-none z-10" />
      ) : overlayShade === 'medium' ? (
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent pointer-events-none z-10" />
      ) : overlayShade === 'bottom' ? (
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none z-10" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent pointer-events-none z-10" />
      )}

      {/* 3. Anchored Text Content - Locked directly inside the image bounds with exact justification! */}
      {textPos !== 'none' && (
        <div className={`absolute inset-0 flex flex-col justify-center p-6 md:p-8 z-20 pointer-events-none ${
          textPos === 'left' ? 'items-start text-left justify-center' :
          textPos === 'right' ? 'items-end text-right justify-center' :
          'items-center text-center justify-center'
        }`}>
            <div className={`w-full max-w-md text-white ${
              textPos === 'left' ? 'text-left items-start' :
              textPos === 'right' ? 'text-right items-end ml-auto' :
              'text-center items-center mx-auto'
            } flex flex-col pointer-events-auto`}>
              {banner.badgeText && (
                <div className={`w-full flex ${
                  textPos === 'left' ? 'justify-start' :
                  textPos === 'right' ? 'justify-end' :
                  'justify-center'
                } mb-2`}>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold shadow-xs ${banner.badgeColor || 'bg-blue-600'}`}>
                    {banner.badgeText}
                  </span>
                </div>
              )}
              
              {(banner.showTitle !== false && banner.show_title !== false) && banner.title && (
                <h2 className={`w-full text-xl sm:text-2xl md:text-3xl font-bold mb-1.5 leading-tight drop-shadow-md ${
                  textPos === 'left' ? 'text-left' :
                  textPos === 'right' ? 'text-right' :
                  'text-center'
                }`}>
                  {banner.title}
                </h2>
              )}
              
              {banner.description && (
                <p className={`w-full text-xs sm:text-sm text-gray-100 mb-3 line-clamp-2 drop-shadow ${
                  textPos === 'left' ? 'text-left' :
                  textPos === 'right' ? 'text-right' :
                  'text-center'
                }`}>
                  {banner.description}
                </p>
              )}

              {hasButton && (
                <div className={`w-full flex ${
                  textPos === 'left' ? 'justify-start' :
                  textPos === 'right' ? 'justify-end' :
                  'justify-center'
                }`}>
                  {banner.actionType === 'link' ? (
                    <Link 
                      to={banner.targetLink || '#'} 
                      onClick={handleClick}
                    >
                      <Button variant="primary" size="sm" className="bg-white text-black hover:bg-gray-100 border-none shadow-md">
                        {banner.actionButtonText}
                        <Icon name="ArrowRight" className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  ) : (
                     <Button 
                       variant="primary" 
                       size="sm" 
                       onClick={handleClick}
                       className="bg-white text-black hover:bg-gray-100 border-none shadow-md"
                     >
                        {banner.actionButtonText}
                     </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      {/* Subtle Ad tag */}
      <div className="absolute top-2 right-2 bg-black/40 px-1.5 py-0.5 rounded text-[10px] text-white/80 uppercase tracking-wider z-10 pointer-events-none">
        Ad
      </div>
    </div>
  );

  if (!hasButton && banner.targetLink && banner.actionType === 'link') {
    return (
      <Link 
        to={banner.targetLink} 
        onClick={handleClick} 
        className={`h-full ${isFullWidth ? 'w-full min-w-full' : 'w-auto flex-shrink-0'} block no-underline snap-start`}
      >
        {PanelContent}
      </Link>
    );
  }

  return (
    <div className={`h-full ${isFullWidth ? 'w-full min-w-full' : 'w-auto flex-shrink-0'} snap-start`}>
      {PanelContent}
    </div>
  );
};

const InlineBanner = ({ banner, banners, isAuthenticated, onAuthRequired }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Extract banner list
  const bannerList = banners || (banner?.isMultiBanner ? banner.banners : (banner ? [banner] : []));
  if (bannerList.length === 0) return null;

  const isMulti = bannerList.length > 1;

  // Determine if half-width layout (2 ads side-by-side on desktop)
  const isHalfWidth = bannerList.some(b => (b.adWidth || b.ad_width) === 'half') || (isMulti && !bannerList.some(b => (b.adWidth || b.ad_width) === 'full'));

  // Symmetrical Round-Robin Infinite Forever Scroller:
  // Loops ads in round-robin order [Ad1, Ad2, Ad1, Ad2, Ad1, Ad2] so next ads repeat continuously!
  const loopBanners = isMulti 
    ? [...bannerList, ...bannerList, ...bannerList] 
    : bannerList;

  // Resolve uniform track height across all banners in this inline slot
  const trackHeight = (() => {
    for (const b of bannerList) {
      const h = parseHeight(b.heightStyle || b.height_style);
      if (h && h !== 'row-height' && h !== 'auto') {
        return h;
      }
    }
    if (bannerList.some(b => (b.heightStyle || b.height_style)?.startsWith('auto'))) {
      return 'auto';
    }
    return isHalfWidth ? '360px' : '320px';
  })();

  const [isHovered, setIsHovered] = useState(false);

  // Initialize scroll position to the middle duplicate set for seamless bi-directional infinite looping
  useEffect(() => {
    if (!isMulti || !scrollRef.current) return;
    const el = scrollRef.current;
    const singleSetWidth = el.scrollWidth / 3;
    if (singleSetWidth > 0 && el.scrollLeft < 10) {
      el.scrollLeft = singleSetWidth;
    }
  }, [isMulti, bannerList.length]);

  // Infinite Seamless Loop Scroll Reset
  const handleScroll = () => {
    if (!isMulti || !scrollRef.current) return;
    const el = scrollRef.current;
    const singleSetWidth = el.scrollWidth / 3;
    if (singleSetWidth <= 0) return;

    if (el.scrollLeft >= singleSetWidth * 2) {
      el.scrollLeft -= singleSetWidth;
    } else if (el.scrollLeft <= 5) {
      el.scrollLeft += singleSetWidth;
    }
  };

  // Step scroll forward / backward in round-robin sequence
  const scrollStep = (direction) => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const stepSize = isHalfWidth ? (el.clientWidth / 2) : el.clientWidth;
    el.scrollBy({
      left: direction === 'left' ? -stepSize : stepSize,
      behavior: 'smooth'
    });
  };

  // Forever Scroller Auto-Advance Timer (Pauses on hover)
  useEffect(() => {
    if (!isMulti || isHovered) return;
    const duration = Math.max(3, (bannerList[0]?.duration || 5)) * 1000;
    const timer = setInterval(() => {
      scrollStep('right');
    }, duration);

    return () => clearInterval(timer);
  }, [isMulti, isHovered, bannerList, isHalfWidth]);

  return (
    <div 
      className="col-span-full w-full my-6 rounded-2xl overflow-hidden shadow-sm border border-slate-200/80 bg-slate-900/5 relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        height: trackHeight,
      }}
    >
      {/* Scrollable Track - Zero registration between ads */}
      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="w-full h-full flex flex-nowrap overflow-x-auto scroll-smooth snap-x snap-mandatory divide-x divide-slate-200/40"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {loopBanners.map((b, idx) => (
          <BannerAdPanel 
            key={`${b.id || 'ad'}-${idx}`}
            banner={b} 
            isAuthenticated={isAuthenticated} 
            onAuthRequired={onAuthRequired} 
            isMulti={isMulti} 
            trackHeight={trackHeight}
          />
        ))}
      </div>

      {/* Navigation Scroll Buttons (Visible on hover when multiple ads in rotation) */}
      {isMulti && (
        <>
          <button
            type="button"
            onClick={() => scrollStep('left')}
            className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/80 text-white backdrop-blur-md transition-all shadow-md z-30 cursor-pointer opacity-0 group-hover:opacity-100 duration-200"
            aria-label="Previous ad"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          <button
            type="button"
            onClick={() => scrollStep('right')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/80 text-white backdrop-blur-md transition-all shadow-md z-30 cursor-pointer opacity-0 group-hover:opacity-100 duration-200"
            aria-label="Next ad"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </>
      )}
    </div>
  );
};

export default InlineBanner;
